import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Popconfirm,
  Card,
  Tag,
  Spin,
  Upload,
  TableColumnsType,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { getGradeList, createGrade, updateGrade, deleteGrade, reviewGrade, importGrades, batchDeleteGrades, batchReviewGrades } from '../../services/grade';
import { getStudentList } from '../../services/student';
import { Grade, Student, PaginatedResponse } from '../../types';
import { antdMessage } from '../../utils/antdGlobal';
import { readCsvText, splitCsvLines, parseCsvLine } from '../../utils/csv';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { get } from '../../utils/request';

const { Option } = Select;
const { Dragger } = Upload;

// 获取当前用户角色
const getUserRole = (): string => {
  const userInfo = localStorage.getItem('userInfo');
  if (userInfo) {
    return JSON.parse(userInfo).role || '';
  }
  return '';
};

const GradeManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponse<Grade> | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGrade, setEditingGrade] = useState<Grade | null>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number; errors: any[] } | null>(null);
  const [batchDeleteResult, setBatchDeleteResult] = useState<{ success: number; failed: number; failedDetails: { id: string; reason: string }[]; total: number } | null>(null);
  const [batchReviewResult, setBatchReviewResult] = useState<{ success: number; failed: number; failedDetails: { id: string; reason: string }[]; total: number } | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchReviewing, setBatchReviewing] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const { isMobile } = useBreakpoint();
  const location = useLocation();
  
  // 判断当前是否为"成绩审核"页面（路径包含 grade-review）
  const isReviewPage = location.pathname.includes('grade-review');
  
  // 从 URL 查询参数读取状态过滤（支持从统计页面跳转时携带 status）
  const searchParams = new URLSearchParams(location.search);
  const hasUrlStatus = searchParams.has('status');
  const urlStatus = searchParams.get('status');
  
  // 判断是否为校区负责人（可以审核）
  const isCampusAdmin = getUserRole() === 'campus_admin';

  const fetchData = async (params?: any) => {
    setLoading(true);
    try {
      const [gradeRes, studentRes] = await Promise.all([
        getGradeList({
          page: pagination.current,
          pageSize: pagination.pageSize,
          // URL 参数优先（空值=不过滤）> 路径判断 > 默认值
          ...(hasUrlStatus
            ? (urlStatus ? { status: urlStatus } : {})
            : isReviewPage
              ? { status: 'pending' }
              : { status: 'approved' }
          ),
          keyword: searchKeyword || undefined,
          ...params,
        }),
        getStudentList({ pageSize: 1000 }),
      ]);
      setData(gradeRes);
      setStudents(studentRes.list);
    } catch (error) {
      antdMessage.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, searchKeyword]);

  // 清除选中状态
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [data]);

  const handleAdd = () => {
    setEditingGrade(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Grade) => {
    setEditingGrade(record);
    form.setFieldsValue({
      ...record,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGrade(id);
      antdMessage.success('删除成功');
      fetchData();
    } catch (error) {
      antdMessage.error('删除失败');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    setBatchDeleting(true);
    try {
      const result = await batchDeleteGrades(selectedRowKeys as string[]);
      setBatchDeleteResult(result);
      if (result.failed === 0) {
        antdMessage.success(`成功删除 ${result.success} 条成绩`);
        setSelectedRowKeys([]);
        fetchData();
      }
    } catch (error) {
      antdMessage.error('批量删除失败');
    } finally {
      setBatchDeleting(false);
    }
  };

  const handleBatchReview = async (status: 'approved' | 'rejected') => {
    if (selectedRowKeys.length === 0) return;
    setBatchReviewing(true);
    try {
      const result = await batchReviewGrades(selectedRowKeys as string[], status);
      setBatchReviewResult(result);
      if (result.failed === 0) {
        antdMessage.success(status === 'approved' ? `成功通过 ${result.success} 条成绩` : `已驳回 ${result.success} 条成绩`);
        setSelectedRowKeys([]);
        fetchData();
      }
    } catch (error) {
      antdMessage.error('批量审核失败');
    } finally {
      setBatchReviewing(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingGrade) {
        await updateGrade(editingGrade.id, values);
        antdMessage.success('更新成功');
      } else {
        await createGrade(values);
        antdMessage.success('录入成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      antdMessage.error(editingGrade ? '更新失败' : '录入失败');
    }
  };

  const handleReview = async (record: Grade, status: 'approved' | 'rejected') => {
    try {
      const rejectReason = status === 'rejected' ? '不符合要求' : undefined;
      await reviewGrade(record.id, status, rejectReason);
      antdMessage.success(status === 'approved' ? '审核通过' : '已驳回');
      fetchData();
    } catch (error) {
      antdMessage.error('操作失败');
    }
  };

  // 下载导入模板
  const handleDownloadTemplate = () => {
    const template = `学号,学期,课程名称,课程代码,课程类别,学分,学时,成绩类型,成绩
STU001,2024-2025第1学期,高等数学,A001,专业课,4,64,percentage,85
STU002,2024-2025第1学期,大学英语,A002,专业课,3,48,percentage,78
STU001,2024-2025第1学期,计算机基础,A003,必修课,2,32,percentage,92`;
    const blob = new Blob(['\ufeff' + template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = '成绩导入模板.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  /**
   * 处理文件导入
   * 编码自动检测（UTF-8/GBK），引号字段兼容，跳过行号纳入失败明细
   */
  const handleImport = async (file: File) => {
    if (!file) return;

    try {
      const content = await readCsvText(file);
      const lines = splitCsvLines(content);
      if (lines.length < 2) {
        antdMessage.error('CSV文件内容为空或格式不正确');
        return;
      }

      // 解析CSV
      const headers = parseCsvLine(lines[0]).map(h => h.trim());
      const grades: any[] = [];
      // 前端解析阶段被跳过的行，与后端失败明细合并展示（行号为文件数据行号）
      const skippedErrors: { row: number; message: string }[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCsvLine(lines[i]).map(v => v.trim());
        if (values.length < headers.length) {
          skippedErrors.push({ row: i, message: `列数不足（${values.length}/${headers.length} 列），已跳过` });
          continue;
        }

        const grade: any = {};
        headers.forEach((header, index) => {
          const key = header.toLowerCase().replace(/ /g, '');
          switch (key) {
            case '学号':
              grade.studentNo = values[index];
              break;
            case '学期':
              grade.semester = values[index];
              break;
            case '课程名称':
              grade.courseName = values[index];
              break;
            case '课程代码':
              grade.courseCode = values[index];
              break;
            case '课程类别':
              grade.courseCategory = values[index];
              break;
            case '学分':
              grade.credits = parseFloat(values[index]) || 0;
              break;
            case '学时':
              grade.hours = parseInt(values[index]) || 0;
              break;
            case '成绩类型':
              grade.gradeType = values[index] === '百分制' || values[index] === 'percentage' ? 'percentage' : 'level';
              break;
            case '成绩':
              grade.gradeValue = values[index];
              break;
          }
        });

        // 验证必填字段
        if (grade.studentNo && grade.semester && grade.courseName && grade.courseCode) {
          grades.push(grade);
        } else {
          skippedErrors.push({ row: i, message: '学号/学期/课程名称/课程代码缺失，已跳过' });
        }
      }

      if (grades.length === 0) {
        if (skippedErrors.length > 0) {
          setImportResult({ success: 0, failed: skippedErrors.length, errors: skippedErrors });
          setImportModalVisible(false);
        }
        antdMessage.error('没有找到有效的数据行');
        return;
      }

      setImportLoading(true);
      try {
        const result = await importGrades(grades);
        // 合并前端跳过行与后端失败明细，保证总数与文件行数一致
        const merged = {
          success: result.success,
          failed: result.failed + skippedErrors.length,
          errors: [...skippedErrors, ...(result.errors || [])],
        };
        setImportResult(merged);
        setImportModalVisible(false);
        if (merged.failed === 0) {
          antdMessage.success(`成功导入 ${result.success} 条成绩记录`);
        } else {
          antdMessage.warning(`导入完成，成功 ${result.success} 条，失败 ${merged.failed} 条`);
        }
        fetchData();
      } catch (error: any) {
        antdMessage.error(error.message || '导入失败');
      } finally {
        setImportLoading(false);
      }
    } catch (error) {
      antdMessage.error('解析文件失败，请检查文件格式');
    }
    return false; // 阻止默认上传
  };

  const columns = [
    {
      title: '学生',
      dataIndex: 'studentName',
      key: 'studentName',
      render: (name: string, record: Grade) => `${name} (${record.studentNo})`,
    },
    {
      title: '学期',
      dataIndex: 'semester',
      key: 'semester',
    },
    {
      title: '课程',
      dataIndex: 'courseName',
      key: 'courseName',
    },
    {
      title: '学分',
      dataIndex: 'credits',
      key: 'credits',
    },
    {
      title: '成绩',
      key: 'grade',
      render: (_: any, record: Grade) => `${record.gradeValue} (${record.gradeType === 'percentage' ? '百分制' : '等级制'})`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          pending: { color: 'warning', text: '待审核' },
          approved: { color: 'success', text: '已通过' },
          rejected: { color: 'error', text: '已驳回' },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Grade) => {
        // 获取当前用户ID
        const currentUserId = JSON.parse(localStorage.getItem('userInfo') || '{}').id;
        const userRole = getUserRole();
        
        // 校区负责人可以管理本校所有待审核成绩（编辑/审核）
        // 上传专员只能管理自己录入的待审核成绩
        const canManage = userRole === 'campus_admin' 
          ? (record.status === 'pending')
          : (userRole === 'uploader' && record.submittedBy === currentUserId && record.status === 'pending');
        
        // 删除权限：校区负责人可删除本校任何状态的成绩，上传专员只能删自己录入的（但不能删已审核通过）
        const canDelete = userRole === 'campus_admin' || userRole === 'super_admin'
          ? true
          : (userRole === 'uploader' && record.submittedBy === currentUserId && record.status !== 'approved');
        
        return (
          <Space size="small">
            {/* 只有校区负责人才能审核 */}
            {isCampusAdmin && record.status === 'pending' && (
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => handleReview(record, 'approved')}
                >
                  通过
                </Button>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleReview(record, 'rejected')}
                >
                  驳回
                </Button>
              </>
            )}
            {/* 校区负责人和上传专员都可以编辑和删除（但条件不同） */}
            {canManage && (
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                >
                  编辑
                </Button>
              </>
            )}
            {canDelete && (
              <Popconfirm
                title={record.status === 'approved' ? `确定要删除这条已审核通过的成绩吗？此操作不可恢复！` : `确定删除该成绩吗？`}
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        title={isReviewPage ? '成绩审核' : '成绩管理'}
        extra={
          <Space wrap>
            <Input.Search
              placeholder="搜索学生姓名/学号"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onSearch={() => { setPagination(p => ({ ...p, current: 1 })); }}
              allowClear
              style={{ width: 200 }}
            />
            <Button icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>
              批量导入
            </Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 条成绩吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />} loading={batchDeleting}>
                  删除选中 ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            )}
            {isReviewPage && selectedRowKeys.length > 0 && (
              <>
                <Popconfirm
                  title={`确定要通过选中的 ${selectedRowKeys.length} 条成绩吗？`}
                  onConfirm={() => handleBatchReview('approved')}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button type="primary" icon={<CheckOutlined />} loading={batchReviewing}>
                    批量通过 ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={`确定要驳回选中的 ${selectedRowKeys.length} 条成绩吗？`}
                  onConfirm={() => handleBatchReview('rejected')}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button danger icon={<CloseOutlined />} loading={batchReviewing}>
                    批量驳回 ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              </>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              录入成绩
            </Button>
          </Space>
        }
      >
        {isMobile ? (
          <Spin spinning={loading}>
            <div>
              {data?.list?.map((record: Grade) => {
                const statusMap: Record<string, { color: string; text: string }> = {
                  pending: { color: 'warning', text: '待审核' },
                  approved: { color: 'success', text: '已通过' },
                  rejected: { color: 'error', text: '已驳回' },
                };
                const { color, text } = statusMap[record.status] || { color: 'default', text: record.status };

                // 获取当前用户ID
                const currentUserId = JSON.parse(localStorage.getItem('userInfo') || '{}').id;
                const userRole = getUserRole();

                // 校区负责人可以管理本校所有待审核成绩（编辑/审核）
                // 上传专员只能管理自己录入的待审核成绩
                const canManage = userRole === 'campus_admin'
                  ? (record.status === 'pending')
                  : (userRole === 'uploader' && record.submittedBy === currentUserId && record.status === 'pending');

                // 删除权限：校区负责人可删除本校任何状态的成绩，上传专员只能删自己录入的（但不能删已审核通过）
                const canDelete = userRole === 'campus_admin' || userRole === 'super_admin'
                  ? true
                  : (userRole === 'uploader' && record.submittedBy === currentUserId && record.status !== 'approved');

                return (
                  <div key={record.id} style={{ marginBottom: 12 }}>
                    <Card size="small" style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 15 }}>{record.courseName}</div>
                          <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>
                            {record.studentName} · {record.semester}
                          </div>
                          <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>
                            成绩：{record.gradeValue}（{record.gradeType === 'percentage' ? '百分制' : '等级制'}）· {record.credits} 学分
                          </div>
                        </div>
                        <Tag color={color}>{text}</Tag>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {/* 只有校区负责人才能审核 */}
                        {isCampusAdmin && record.status === 'pending' && (
                          <>
                            <Button size="small" icon={<CheckOutlined />} onClick={() => handleReview(record, 'approved')}>通过</Button>
                            <Button size="small" danger icon={<CloseOutlined />} onClick={() => handleReview(record, 'rejected')}>驳回</Button>
                          </>
                        )}
                        {/* 校区负责人和上传专员都可以编辑（仅待审核） */}
                        {canManage && (
                          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                        )}
                        {/* 删除权限独立：校区负责人/超级管理员可删任何状态，上传专员只能删自己录入的未通过成绩 */}
                        {canDelete && (
                          <Popconfirm
                            title={record.status === 'approved' ? `确定要删除这条已审核通过的成绩吗？此操作不可恢复！` : `确定删除该成绩吗？`}
                            onConfirm={() => handleDelete(record.id)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                          </Popconfirm>
                        )}
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          </Spin>
        ) : (
          <Table
            columns={columns}
            dataSource={data?.list}
            loading={loading}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data?.total,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize: pageSize || 10 });
              },
            }}
          />
        )}
      </Card>

      <Modal
        title={editingGrade ? '编辑成绩' : '录入成绩'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 0, maxWidth: '100vw' } : {}}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="studentId"
            label="学生"
            rules={[{ required: true, message: '请选择学生' }]}
          >
            <Select placeholder="请选择学生" showSearch optionFilterProp="children">
              {students.map((student) => (
                <Option key={student.id} value={student.id}>
                  {student.name} ({student.studentNo})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="semester"
            label="学期"
            rules={[{ required: true, message: '请输入学期' }]}
          >
            <Input placeholder="如：2024-2025第1学期" />
          </Form.Item>

          <Form.Item
            name="courseName"
            label="课程名称"
            rules={[{ required: true, message: '请输入课程名称' }]}
          >
            <Input placeholder="请输入课程名称" />
          </Form.Item>

          <Form.Item
            name="courseCode"
            label="课程代码"
            rules={[{ required: true, message: '请输入课程代码' }]}
          >
            <Input placeholder="请输入课程代码" />
          </Form.Item>

          <Form.Item
            name="courseCategory"
            label="课程类别"
            rules={[{ required: true, message: '请选择课程类别' }]}
          >
            <Select placeholder="请选择课程类别">
              <Option value="专业课">专业课</Option>
              <Option value="选修课">选修课</Option>
              <Option value="实践课">实践课</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="credits"
            label="学分"
            rules={[{ required: true, message: '请输入学分' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} max={20} step={0.5} placeholder="请输入学分" />
          </Form.Item>

          <Form.Item
            name="hours"
            label="学时"
            rules={[{ required: true, message: '请输入学时' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} max={200} placeholder="请输入学时" />
          </Form.Item>

          <Form.Item
            name="gradeType"
            label="成绩类型"
            rules={[{ required: true, message: '请选择成绩类型' }]}
          >
            <Select placeholder="请选择成绩类型">
              <Option value="percentage">百分制</Option>
              <Option value="level">等级制</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="gradeValue"
            label="成绩"
            rules={[{ required: true, message: '请输入成绩' }]}
          >
            <Input placeholder="百分制(0-100) 或 等级制(优秀/良好/中等/及格/不及格)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入 Modal */}
      <Modal
        title="批量导入成绩"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            下载导入模板
          </Button>
          <span style={{ marginLeft: 16, color: '#888' }}>
            请先下载模板，按格式填写成绩数据后上传
          </span>
        </div>

        <Dragger
          accept=".csv"
          showUploadList={{ showRemoveIcon: false }}
          beforeUpload={handleImport}
        >
          <p className="ant-upload-drag-icon">
            {importLoading ? <span>正在导入...</span> : <UploadOutlined />}
          </p>
          {importLoading ? (
            <p className="ant-upload-text">正在处理，请稍候...</p>
          ) : (
            <>
              <p className="ant-upload-text">点击或拖拽CSV文件到此处上传</p>
              <p className="ant-upload-hint">支持 CSV 格式文件，请确保文件编码为 UTF-8</p>
            </>
          )}
        </Dragger>

        <div style={{ marginTop: 16, padding: '12px', background: '#f5f5f5', borderRadius: 8 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>模板字段说明：</div>
          <ul style={{ margin: 0, paddingLeft: 20, color: '#666', fontSize: 13 }}>
            <li>学号：学生的唯一标识</li>
            <li>学期：格式如 2024-2025第1学期</li>
            <li>课程名称：课程的具体名称</li>
            <li>课程代码：课程的编码</li>
            <li>课程类别：专业课/选修课/实践课</li>
            <li>学分：数值，如 4</li>
            <li>学时：数值，如 64</li>
            <li>成绩类型：percentage（百分制）/ level（等级制）</li>
            <li>成绩：百分制填数字，等级制填优秀/良好/中等/及格/不及格</li>
          </ul>
        </div>
      </Modal>

      {/* 导入结果 Modal */}
      <Modal
        title="导入结果"
        open={!!importResult}
        onCancel={() => setImportResult(null)}
        footer={
          <Button type="primary" onClick={() => setImportResult(null)}>
            确定
          </Button>
        }
        width={500}
      >
        {importResult && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Tag color="success" style={{ marginRight: 8 }}>成功: {importResult.success} 条</Tag>
              {importResult.failed > 0 && <Tag color="error">失败: {importResult.failed} 条</Tag>}
            </div>
            {importResult.errors.length > 0 && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>失败记录：</div>
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table
                    size="small"
                    dataSource={importResult.errors}
                    rowKey={(record: any, index) => index?.toString() || 'error'}
                    pagination={false}
                    columns={[
                      { title: '行号', dataIndex: 'row', width: 60 },
                      { title: '错误原因', dataIndex: 'message' },
                    ]}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 批量删除结果 Modal */}
      <Modal
        title="批量删除结果"
        open={!!batchDeleteResult}
        onCancel={() => setBatchDeleteResult(null)}
        footer={
          <Button type="primary" onClick={() => {
            setBatchDeleteResult(null);
            setSelectedRowKeys([]);
            fetchData();
          }}>
            确定
          </Button>
        }
        width={500}
      >
        {batchDeleteResult && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Tag color="success" style={{ marginRight: 8 }}>成功: {batchDeleteResult.success} 条</Tag>
              {batchDeleteResult.failed > 0 && <Tag color="error">失败: {batchDeleteResult.failed} 条</Tag>}
            </div>
            {batchDeleteResult.failedDetails && batchDeleteResult.failedDetails.length > 0 && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>无法删除的记录：</div>
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  {batchDeleteResult.failedDetails.map((item, index) => (
                    <div key={index} style={{ padding: '4px 0', fontSize: 13, color: '#666', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ color: '#999', marginRight: 8 }}>#{index + 1}</span>
                      {item.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 批量审核结果 Modal */}
      <Modal
        title="批量审核结果"
        open={!!batchReviewResult}
        onCancel={() => setBatchReviewResult(null)}
        footer={
          <Button type="primary" onClick={() => {
            setBatchReviewResult(null);
            setSelectedRowKeys([]);
            fetchData();
          }}>
            确定
          </Button>
        }
        width={500}
      >
        {batchReviewResult && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Tag color="success" style={{ marginRight: 8 }}>成功: {batchReviewResult.success} 条</Tag>
              {batchReviewResult.failed > 0 && <Tag color="error">失败: {batchReviewResult.failed} 条</Tag>}
            </div>
            {batchReviewResult.failedDetails && batchReviewResult.failedDetails.length > 0 && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>无法审核的记录：</div>
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  {batchReviewResult.failedDetails.map((item, index) => (
                    <div key={index} style={{ padding: '4px 0', fontSize: 13, color: '#666', borderBottom: '1px solid #f0f0f0' }}>
                      <span style={{ color: '#999', marginRight: 8 }}>#{index + 1}</span>
                      {item.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GradeManagement;
